import React, { useEffect } from 'react';
import type { FoundryResponseDetails } from '../types';
import './FoundryTraceOverlay.css';

interface FoundryTraceOverlayProps {
  details: FoundryResponseDetails | null;
  loading: boolean;
  error: string | null;
  onClose: () => void;
}

const formatDuration = (durationMs?: number): string => {
  if (!durationMs) return '';
  if (durationMs < 1000) return `${durationMs} ms`;
  return `${(durationMs / 1000).toFixed(1)} s`;
};

const formatTimestamp = (value?: string): string => {
  if (!value) return '';
  return new Date(value).toLocaleString();
};

const renderRaw = (value: unknown): string => {
  if (value == null) return '';
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

const FoundryTraceOverlay: React.FC<FoundryTraceOverlayProps> = ({ details, loading, error, onClose }) => {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div className="trace-overlay-backdrop" role="dialog" aria-modal="true" aria-label="Foundry trace details" onClick={onClose}>
      <div className="trace-overlay" onClick={(event) => event.stopPropagation()}>
        <button className="trace-overlay__close" type="button" aria-label="Close trace" onClick={onClose}>
          <i className="modus-icons" aria-hidden="true">close</i>
        </button>
        <div className="trace-overlay__scroll">
          <div className="trace-overlay__header">
            <div>
              <h2>Trace</h2>
              {details?.responseId && <p>{details.responseId}</p>}
            </div>
            {details && (
              <div className="trace-overlay__meta">
                {details.status && <span>{details.status}</span>}
                {details.agentName && <span>{details.agentName}</span>}
                {details.model && <span>{details.model}</span>}
                {details.durationMs ? <span>{formatDuration(details.durationMs)}</span> : null}
              </div>
            )}
          </div>

          {loading && <div className="trace-overlay__state">Loading trace...</div>}
          {error && !loading && <div className="trace-overlay__state trace-overlay__state--error">{error}</div>}

          {!loading && !error && details && (
            <>
              <section className="trace-overlay__section">
                <h3>Run</h3>
                <div className="trace-overlay__grid">
                  <div>
                    <span>Started</span>
                    <strong>{formatTimestamp(details.startedAt) || 'n/a'}</strong>
                  </div>
                  <div>
                    <span>Completed</span>
                    <strong>{formatTimestamp(details.completedAt) || 'n/a'}</strong>
                  </div>
                  <div>
                    <span>Error</span>
                    <strong>{details.error || 'none'}</strong>
                  </div>
                </div>
              </section>

              {details.finalOutput && (
                <section className="trace-overlay__section">
                  <h3>Final output</h3>
                  <pre className="trace-overlay__pre">{details.finalOutput}</pre>
                </section>
              )}

              <section className="trace-overlay__section">
                <h3>Tools</h3>
                <div className="trace-overlay__tools">
                  {(details.toolCalls ?? []).length === 0 && <div className="trace-overlay__empty">No tool calls recorded.</div>}
                  {(details.toolCalls ?? []).map((toolCall, index) => (
                    <article key={`${toolCall.name ?? 'tool'}-${index}`} className="trace-overlay__tool-card">
                      <div className="trace-overlay__tool-header">
                        <strong>{toolCall.name || 'Tool call'}</strong>
                        <span>{toolCall.status || 'unknown'}</span>
                      </div>
                      <div className="trace-overlay__tool-meta">
                        {toolCall.durationMs ? <span>{formatDuration(toolCall.durationMs)}</span> : null}
                        {toolCall.startedAt ? <span>{formatTimestamp(toolCall.startedAt)}</span> : null}
                      </div>
                      {toolCall.argumentsPreview && (
                        <div>
                          <h4>Arguments</h4>
                          <pre className="trace-overlay__pre trace-overlay__pre--compact">{toolCall.argumentsPreview}</pre>
                        </div>
                      )}
                      {toolCall.resultPreview && (
                        <div>
                          <h4>Result</h4>
                          <pre className="trace-overlay__pre trace-overlay__pre--compact">{toolCall.resultPreview}</pre>
                        </div>
                      )}
                      {toolCall.error && <div className="trace-overlay__tool-error">{toolCall.error}</div>}
                    </article>
                  ))}
                </div>
              </section>

              <details className="trace-overlay__section">
                <summary>Raw details</summary>
                <pre className="trace-overlay__pre">{renderRaw(details.raw)}</pre>
              </details>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FoundryTraceOverlay;